import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Table,
  Image,
  Modal,
} from 'react-bootstrap';
import axios from 'axios';
import { FaTrash, FaEdit } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ProductImages = () => {
  const [products, setProducts] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [selectedFloorFiles, setSelectedFloorFiles] = useState([]); // New state for floor images
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState(''); // 'add', 'edit', 'delete'
  const [currentImage, setCurrentImage] = useState(null);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const imagePath = 'https://api.biznetusa.com/uploads/products/';

  // Fetch products to populate dropdown
  useEffect(() => {
    axios
      .get('https://api.biznetusa.com/api/get-products')
      .then((response) => setProducts(response.data.products))
      .catch((error) => {
        console.error('Error fetching products:', error);
        toast.error('Failed to fetch products.');
      });
  }, []);

  // Fetch product images
  useEffect(() => {
    fetchProductImages();
  }, []);

  const fetchProductImages = () => {
    axios
      .get('https://api.biznetusa.com/api/get-productimages')
      .then((response) => setProductImages(response.data.products))
      .catch((error) => {
        console.error('Error fetching product images:', error);
        toast.error('Failed to fetch product images.');
      });
  };

  // Handle file selection for adding images
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const handleFloorFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFloorFiles((prevFiles) => [...prevFiles, ...files]);
  };

  // Handle file selection for editing image
  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditImageFile(file);
      setEditImagePreview(URL.createObjectURL(file));
    }
  };

  // Remove selected file before uploading (Add Mode)
  const removeSelectedFile = (index) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
  };

  const removeSelectedFloorFile = (index) => {
    const updatedFiles = selectedFloorFiles.filter((_, i) => i !== index);
    setSelectedFloorFiles(updatedFiles);
  };

  // Remove selected file before uploading (Edit Mode)
  const removeEditImage = () => {
    setEditImageFile(null);
    setEditImagePreview(null);
  };

  // Handle showing the modal for add, edit, or delete
  const handleShowModal = (mode, img = null) => {
    setModalMode(mode);
    setShowModal(true);

    if (mode === 'edit' && img) {
      setCurrentImage(img);
      setEditImagePreview(`${imagePath}${img.image}`);
    }

    if (mode === 'delete' && img) {
      setCurrentImage(img);
    }

    if (mode === 'add') {
      setSelectedProductId('');
      setSelectedFiles([]);
      setSelectedFloorFiles([]); // Reset floor files

    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    setModalMode('');
    setCurrentImage(null);
    setSelectedProductId('');
    setSelectedFiles([]);
    setSelectedFloorFiles([]); // Reset floor files
    setEditImageFile(null);
    setEditImagePreview(null);
  };

  // Handle form submission for adding or editing images
  const handleSubmit = (e) => {
    e.preventDefault();

    if (modalMode === 'add') {
      // Add Mode
      if (!selectedProductId || selectedFiles.length === 0 || selectedFloorFiles.length === 0) {
        toast.error('Please select a product and at least one image.');
        return;
      }

      const formData = new FormData();
      formData.append('pd_id', selectedProductId);
      selectedFiles.forEach((file) => {
        formData.append('image[]', file);
      });
      selectedFloorFiles.forEach((file) => {
        formData.append('floor_image', file);
      });

      axios
        .post('https://api.biznetusa.com/api/store-productimage', formData)
        .then(() => {
          toast.success('Images uploaded successfully!');
          fetchProductImages();
          handleCloseModal();
        })
        .catch((error) => {
          console.error('Error uploading images:', error);
          toast.error('Failed to upload images.');
        });
    } else if (modalMode === 'edit') {
      // Edit Mode
      if (!editImageFile) {
        toast.error('Please select an image to update.');
        return;
      }

      const formData = new FormData();
      formData.append('image', editImageFile);

      axios
        .post(
          `https://donerightdecals.com/api/update-productimage/${currentImage.id}`,
          formData
        )
        .then(() => {
          toast.success('Image updated successfully!');
          fetchProductImages();
          handleCloseModal();
        })
        .catch((error) => {
          console.error('Error updating image:', error);
          toast.error('Failed to update image.');
        });
    }
  };

  // Handle image deletion
  const handleDeleteImage = () => {
    if (!currentImage) {
      toast.error('No image selected for deletion.');
      return;
    }

    axios
      .delete(`https://api.biznetusa.com/api/deleteproductimage/${currentImage.id}`)
      .then(() => {
        toast.success('Image deleted successfully!');
        fetchProductImages();
        handleCloseModal();
      })
      .catch((error) => {
        console.error('Error deleting image:', error);
        toast.error('Failed to delete image.');
      });
  };

  return (
    <Container>
      {/* Toast Container for Notifications */}
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {/* Header */}
      <Row className="my-4">
        <Col>
          <h2>Manage Product Images</h2>
          <Button variant="primary" onClick={() => handleShowModal('add')}>
            Add Images
          </Button>
        </Col>
      </Row>

      {/* Images Table */}
      <Row className="my-4">
        <Col>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Product ID</th>
                <th>Product Title</th>
                <th>Image</th>
                <th>Uploaded At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {productImages.length > 0 ? (
                productImages.map((img) => (
                  <tr key={img.id}>
                    <td>{img.pd_id}</td>
                    <td>
                      {products.find((p) => p.id === img.pd_id)?.title || 'N/A'}
                    </td>
                    <td>
                      <Image
                        src={`${imagePath}${img.image}`}
                        thumbnail
                        width={100}
                        height={100}
                      />
                    </td>
                    <td>
                      {new Date(img.created_at).toLocaleString()}
                    </td>
                    <td>
                      <Button
                        variant="info"
                        className="mr-2"
                        onClick={() => handleShowModal('edit', img)}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => handleShowModal('delete', img)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No images found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Col>
      </Row>

      {/* Modal for Add, Edit, Delete */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'add'
              ? 'Add Product Images'
              : modalMode === 'edit'
              ? 'Edit Product Image'
              : 'Delete Product Image'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMode === 'add' && (
            <Form onSubmit={handleSubmit}>
              {/* Select Product */}
              <Form.Group controlId="formProductSelect">
                <Form.Label>Select Product</Form.Label>
                <Form.Control
                  as="select"
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                  required
                >
                  <option value="">Select a product</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.title}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>

              {/* Upload Images */}
              <Form.Group controlId="formFile">
                <Form.Label>Upload Images</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  accept="image/*"
                />
              </Form.Group>


              <Form.Group controlId="formFloorFile">
                <Form.Label>Upload Floor Images</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={handleFloorFileChange}
                  accept="image/*"
                />
              </Form.Group>

              {/* Preview Selected Files */}
              {selectedFiles.length > 0 && (
                <div className="mt-3">
                  <h5>Selected Images</h5>
                  <div className="d-flex flex-wrap">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="m-2 position-relative"
                      >
                        <Image
                          src={URL.createObjectURL(file)}
                          thumbnail
                          width={100}
                          height={100}
                        />
                        <FaTrash
                          className="position-absolute text-danger"
                          style={{ top: 0, right: 0, cursor: 'pointer' }}
                          onClick={() => removeSelectedFile(index)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}


{selectedFloorFiles.length > 0 && (
                <div className="mt-3">
                  <h5>Selected Floor Images</h5>
                  <div className="d-flex flex-wrap">
                    {selectedFloorFiles.map((file, index) => (
                      <div key={index} className="m-2 position-relative">
                        <Image
                          src={URL.createObjectURL(file)}
                          thumbnail
                          width={100}
                          height={100}
                        />
                        <FaTrash
                          className="position-absolute text-danger"
                          style={{ top: 0, right: 0, cursor: 'pointer' }}
                          onClick={() => removeSelectedFloorFile(index)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}


              <Button variant="primary" type="submit" className="mt-3">
                Upload Images
              </Button>
            </Form>
          )}

          {modalMode === 'edit' && currentImage && (
            <Form onSubmit={handleSubmit}>
              {/* Current Image Preview */}
              <Form.Group>
                <Form.Label>Current Image</Form.Label>
                <div className="mb-3">
                  <Image
                    src={`${imagePath}${currentImage.image}`}
                    thumbnail
                    width={150}
                    height={150}
                  />
                </div>
              </Form.Group>

              {/* Upload New Image */}
              <Form.Group controlId="formEditFile">
                <Form.Label>Upload New Image</Form.Label>
                <Form.Control
                  type="file"
                  onChange={handleEditFileChange}
                  accept="image/*"
                />
              </Form.Group>

              {/* Preview New Image */}
              {editImagePreview && (
                <div className="mt-3 position-relative">
                  <Image
                    src={editImagePreview}
                    thumbnail
                    width={150}
                    height={150}
                  />
                  <FaTrash
                    className="position-absolute text-danger"
                    style={{ top: 0, right: 0, cursor: 'pointer' }}
                    onClick={removeEditImage}
                  />
                </div>
              )}

              <Button variant="primary" type="submit" className="mt-3">
                Update Image
              </Button>
            </Form>
          )}

          {modalMode === 'delete' && currentImage && (
            <div>
              <p>Are you sure you want to delete this image?</p>
              <div className="mb-3">
                <Image
                  src={`${imagePath}${currentImage.image}`}
                  thumbnail
                  width={150}
                  height={150}
                />
              </div>
              <Button variant="danger" onClick={handleDeleteImage}>
                Confirm Delete
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProductImages;
